import { Menu } from 'components/Menu';
import { MenuItem, SubMenuItem } from 'components/Menu/MenuItem';
import { TabPanel, Tabs } from 'components/Tabs';
import { AddUserIcon } from 'components/icons/AddUserIcon';
import { CloseIcon } from 'components/icons/CloseIcon';
import { SearchIcon } from 'components/icons/SearchIcon';
import { VerticalEllipsisIcon } from 'components/icons/VerticalEllipsisIcon';
import { useContext, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import Popover from '@mui/material/Popover';

import { useSelector } from 'react-redux';
import { RootState } from 'store';

import { MODAL_ANIMATIONS, MODAL_TYPES } from '../constants';
import { ModalContext } from 'context/ModalContext';
import { useLocation } from 'react-router-dom';
import NoImg from 'assets/img/no-img.jpg';
import PopoverButton from 'components/Popover';

import { handleSetMembers } from 'store/workspaceMemberSlice';
import { useDispatch } from 'react-redux';

import api from 'services/api';

interface ChannelMembersProps {
  onCancel: () => void;
  selectedChannel: any;
  tab: string;
}

export default function ChannelMembers({ onCancel, selectedChannel, tab }: ChannelMembersProps) {
  const [channelMembers, setChannelMembers] = useState([] as any);
  const location = useLocation();
  const [selected, setSelected] = useState(tab);
  const [searchVal, setSearchVal] = useState('');
  const { showModal } = useContext(ModalContext);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const dispatch = useDispatch();
  const { users: workspaceMembers, isLoaded: workspaceIsLoaded } = useSelector((state: RootState) => state.members);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAddMember = (user: any) => {
    api
      .post(`/api/users/channel/${selectedChannel.hash_id}/invite-member/`, {
        users: [user.user_id]
      })
      .then((res) => {
        if (res?.data?.succeed?.length) {
          setChannelMembers((prev: any) => [...prev, ...res.data.succeed]);
        }
      });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  useEffect(() => {
    let isMounted = false;
    const fetchData = async () => {
      try {
        isMounted = true;
        const res = await api.get(`/api/users/`);
        if (isMounted) {
          dispatch(handleSetMembers(res.data));
        }
      } catch (error) {
        console.error('Error fetching workspace members:', error);
      }
    };

    api.get(`/api/users/channel/${selectedChannel.hash_id}/members/?page_size=all`).then((res) => {
      setChannelMembers(res.data);
    });

    if (!workspaceIsLoaded) {
      fetchData();
    }

    return () => {
      isMounted = false;
    };
  }, []);

  const tabs = [
    {
      index: 'about',
      title: 'About'
    },
    {
      index: 'members',
      title: 'Members'
    }
  ];
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchVal(e.target.value);
  };

  const menus = (user: any) => [
    {
      id: user.id,
      name: 'Remove from channel',
      onClick: () => {
        showModal(MODAL_TYPES.remove_channel_people, {
          selectedChannel,
          tab,
          user
        });
      },
      customClass: 'text-red-main hover:bg-opacity-5 group-hover:text-red-main'
    }
  ];

  const handleInviteModal = () => {
    showModal(MODAL_TYPES.add_people, {
      selectedChannel,
      tab,
      channelMembers
    });
  };

  return (
    <div className="transparent font-lato">
      <div className="border bg-gray-bg-dark border-gray-300 rounded-lg bg-white pb-0 pt-9 w-screen md:w-[622px] md:min-h-[300px]">
        <div className="flex px-7">
          <span className="font-bold text-2xl leading-6 text-[#1A1C1D]"># {selectedChannel?.name}</span>
          <CloseIcon className="ml-auto w-5 h-5 cursor-pointer opacity-70 hover:opacity-100" onClick={onCancel} />
        </div>
        <div className="w-full mt-5">
          <Tabs
            tabs={tabs}
            selected={selected}
            onSwitch={(e) => {
              setSelected(e);
            }}
            tabStyle={2}
            additionalClasses="py-0 pl-4 flex-wrap shadow-tab"
          />
          <div className="h-full relative">
            <TabPanel index="members" active={selected === 'members'}>
              <div className="pb-4">
                <div className="relative mt-4 px-5">
                  <SearchIcon className="absolute left-7 w-[15px] h-[15px] top-3" fill="currentColor" />
                  <input
                    type="text"
                    className="pl-8 py-2 w-2/3 mb-4 text-sm text-[#495057] border focus:ring-0 border-[#ced4da] rounded-md leading-6"
                    value={searchVal}
                    onChange={handleChange}
                    placeholder="Find members"
                  />
                </div>
                <div
                  className="flex px-5 py-3 hover:bg-opacity-5 bg-black bg-opacity-0 cursor-pointer"
                  onClick={() => handleInviteModal()}
                >
                  <div className="bg-blue-menu-active p-1 flex items-center justify-center rounded-md text-white cursor-pointer">
                    <AddUserIcon className="w-6 h-6" />
                  </div>
                  <span className="text-black px-3 py-1 text-base font-bold">Add people</span>
                </div>
                <div className="h-80 overflow-auto relative">
                  {channelMembers
                    .filter(
                      (item: any) =>
                        (item.member.display_name &&
                          item.member.display_name.toLowerCase().includes(searchVal.toLowerCase())) ||
                        (item.member.full_name && item.member.full_name.toLowerCase().includes(searchVal.toLowerCase()))
                    )
                    .sort((a: any, b: any) => a.member.full_name?.localeCompare(b.member.full_name))
                    .map((user: any) => (
                      <div
                        className="flex items-center px-5 py-3 hover:bg-opacity-5 bg-black bg-opacity-0 cursor-pointer relative"
                        key={user.member.user_id}
                      >
                        <img
                          src={user.member.avatar || NoImg}
                          className="w-8 h-8 flex items-center justify-center rounded-full text-white cursor-pointer"
                        />
                        <span className="text-black px-3 py-1 text-base font-bold">{user.member.full_name}</span>
                        <span className="text-gray-main py-1 text-base font-bold">{user.member.display_name}</span>

                        <div className="ml-auto relative" aria-describedby={id} onClick={handleClick}>
                          <PopoverButton
                            buttonData={
                              <VerticalEllipsisIcon className="h-4 ml-4 fill-gray-main mr-4 cursor-pointer" />
                            }
                          >
                            <div
                              className="origin-top-left py-4 rounded-md shadow-pop bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
                              role="menu"
                              aria-orientation="vertical"
                              aria-labelledby="menu-button"
                              tabIndex={-1}
                            >
                              {menus(user).map((el: any) => (
                                <SubMenuItem key={el.id} {...el} />
                              ))}
                            </div>
                          </PopoverButton>
                        </div>
                      </div>
                    ))}
                  {searchVal && (
                    <div className="border-t border-gray-border pt-4">
                      <span className="text-black px-5 py-2 text-sm font-bold">Not in this channel</span>
                      {workspaceMembers
                        .filter(
                          (workspaceMember: any) =>
                            !channelMembers.find((channelMember: any) => channelMember.member.id === workspaceMember.id)
                        )
                        .filter(
                          (item: any) =>
                            (item.display_name && item.display_name.toLowerCase().includes(searchVal.toLowerCase())) ||
                            (item.full_name && item.full_name.toLowerCase().includes(searchVal.toLowerCase()))
                        )
                        .map((user: any) => (
                          <div
                            className="flex items-center px-5 py-3 hover:bg-opacity-5 bg-black bg-opacity-0 relative"
                            key={user.user_id}
                          >
                            <img
                              src={user.avatar || NoImg}
                              className="w-8 h-8 flex items-center justify-center rounded-full text-white cursor-pointer"
                            />
                            <span className="text-black px-3 py-1 text-base font-bold">{user.full_name}</span>
                            <span className="text-gray-main py-1 text-base font-bold">{user.display_name}</span>
                            <div
                              className="ml-auto relative text-blue-menu-active cursor-pointer "
                              aria-describedby={id}
                              onClick={() => handleAddMember(user)}
                            >
                              add
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </TabPanel>
            <TabPanel index="about" active={selected === 'about'}>
              <div className="relative px-4 py-4 bg-opacity-5 bg-black h-full">
                <div className="bg-white">
                  <div className="p-4 rounded-md border bg-black bg-opacity-0 hover:bg-opacity-5 cursor-pointer">
                    <div className="flex justify-between">
                      <span className="font-bold">Channel name</span>
                      <span className="text-blue-menu-active cursor-pointer">Edit</span>
                    </div>
                    <p># {selectedChannel?.name}</p>
                  </div>
                </div>
                <div className="rounded-md bg-white border mt-4">
                  <div className="p-4 bg-black bg-opacity-0 hover:bg-opacity-5 cursor-pointer">
                    <div className="flex justify-between">
                      <span className="font-bold">Topic</span>
                      <span className="text-blue-menu-active cursor-pointer">Edit</span>
                    </div>
                    <p className="text-gray-400 font-bold">Add a topic</p>
                  </div>
                  <div className="p-4 border-t bg-black bg-opacity-0 hover:bg-opacity-5 cursor-pointer">
                    <div className="flex justify-between">
                      <span className="font-bold">Description</span>
                      <span className="text-blue-menu-active cursor-pointer">Edit</span>
                    </div>
                    <p className="text-gray-400 font-bold">Add a description</p>
                  </div>
                  <div className="p-4 border-t bg-black bg-opacity-0 hover:bg-opacity-5 cursor-pointer">
                    <p className="font-bold">Created by</p>
                    <p>Stephen Paek on January 29, 2019</p>
                  </div>
                  <div className="p-4 border-t bg-black bg-opacity-0 hover:bg-opacity-5 cursor-pointer">
                    <p className="font-bold text-red-main">Leave channel</p>
                  </div>
                </div>
              </div>
            </TabPanel>
          </div>
        </div>
      </div>
    </div>
  );
}
